import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, X, Search, Crosshair } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { apiUploadImage } from "../adminApi";

// Anteprima fedele alla card dello storefront (sfondo lime, foto con zoom+posizione).
function Preview({ image, scale, fit, posX, posY }) {
  const cover = fit !== "contain";
  return (
    <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-2xl" style={{ background: "#C6F24E" }}>
      {image ? (
        <img
          src={image}
          alt=""
          className={`absolute inset-0 h-full w-full ${cover ? "object-cover" : "object-contain"}`}
          style={{ objectPosition: `${posX}% ${posY}%`, transform: `scale(${scale})`, transformOrigin: `${posX}% ${posY}%` }}
        />
      ) : (
        <span className="text-sm font-bold text-ink/50">Nessuna foto</span>
      )}
    </div>
  );
}

function SliderRow({ label, value, min, max, step, onChange, onCommit, suffix, testId }) {
  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <Label className="text-xs text-steel">{label}</Label>
        <span className="text-xs tabular-nums text-tertiary">{Math.round(value * (suffix === "%" ? 100 : 1))}{suffix}</span>
      </div>
      <Slider value={[value]} min={min} max={max} step={step}
        onValueChange={(v) => onChange(v[0])} onValueCommit={(v) => onCommit(v[0])} data-testid={testId} />
    </div>
  );
}

function ItemCard({ item, imageField, labelField, uploadFolder, uploadContext, testPrefix, update, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const image = item[imageField] || "";
  const label = item[labelField] || item.key;
  const key = String(item.key || item.id).toLowerCase();

  const [scale, setScale] = useState(Number(item.image_scale) || 1);
  const [posX, setPosX] = useState(item.image_pos_x != null ? Number(item.image_pos_x) : 50);
  const [posY, setPosY] = useState(item.image_pos_y != null ? Number(item.image_pos_y) : 50);
  const fit = item.image_fit || "cover";

  const save = async (patch) => {
    onChange({ ...item, ...patch });
    try {
      await update(item.id, patch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Salvataggio non riuscito");
    }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const res = await apiUploadImage(file, uploadFolder, undefined, uploadContext);
      await save({ [imageField]: res.url });
      toast.success("Foto caricata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Caricamento non riuscito");
    } finally {
      setUploading(false);
    }
  };

  const recenter = () => {
    setScale(1); setPosX(50); setPosY(50);
    save({ image_scale: 1, image_pos_x: 50, image_pos_y: 50 });
  };

  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-line bg-surface2 p-4" data-testid={`${testPrefix}-${key}`}>
      <Preview image={image} scale={scale} fit={fit} posX={posX} posY={posY} />

      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-chalk">{label}</p>
          <p className="text-xs text-steel">{item.product_count ?? item.count ?? 0} prodotti</p>
        </div>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid={`${testPrefix}-input-${key}`} />
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="secondary" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid={`${testPrefix}-upload-${key}`}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {image ? "Sostituisci" : "Carica"}
          </Button>
          {image && (
            <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive" onClick={() => save({ [imageField]: "" })} data-testid={`${testPrefix}-remove-${key}`} aria-label="Rimuovi foto">
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Controlli di proporzione e posizione (attivi sempre) */}
      <div className="flex items-center justify-between gap-2">
        <ToggleGroup type="single" value={fit} onValueChange={(v) => v && save({ image_fit: v })} className="justify-start" data-testid={`${testPrefix}-fit-${key}`}>
          <ToggleGroupItem value="cover" className="h-7 px-2 text-xs">Riempi</ToggleGroupItem>
          <ToggleGroupItem value="contain" className="h-7 px-2 text-xs">Intera</ToggleGroupItem>
        </ToggleGroup>
        <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-xs" onClick={recenter} data-testid={`${testPrefix}-center-${key}`}>
          <Crosshair className="h-3.5 w-3.5" /> Centra
        </Button>
      </div>

      <div className="flex flex-col gap-2.5">
        <SliderRow label="Zoom" value={scale} min={1} max={2.5} step={0.05} suffix="%"
          onChange={setScale} onCommit={(v) => save({ image_scale: v })} testId={`${testPrefix}-scale-${key}`} />
        <SliderRow label="Posizione orizzontale" value={posX} min={0} max={100} step={1} suffix=""
          onChange={setPosX} onCommit={(v) => save({ image_pos_x: v })} testId={`${testPrefix}-posx-${key}`} />
        <SliderRow label="Posizione verticale" value={posY} min={0} max={100} step={1} suffix=""
          onChange={setPosY} onCommit={(v) => save({ image_pos_y: v })} testId={`${testPrefix}-posy-${key}`} />
      </div>
    </div>
  );
}

// Gestore visivo generico per foto (categorie o marchi) con zoom e posizione.
export default function PhotoManager({
  list, update,
  imageField = "image", labelField = "label",
  uploadFolder = "rinova/categories", uploadContext = "card",
  testPrefix = "catphoto", intro,
}) {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    list().then(setItems).catch(() => setItems([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateOne = (updated) => setItems((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));

  if (items === null) {
    return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-tertiary" /></div>;
  }

  const filtered = items.filter((c) => String(c[labelField] || c.key).toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-steel">{intro || "Carica la foto, regola lo zoom e trascina la posizione per centrare il soggetto. L'anteprima mostra esattamente come apparirà sul sito."}</p>
        <div className="relative mt-2 max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tertiary" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca…" className="pl-9" data-testid={`${testPrefix}-search`} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid={`${testPrefix}-grid`}>
        {filtered.map((c) => (
          <ItemCard
            key={c.id}
            item={c}
            imageField={imageField}
            labelField={labelField}
            uploadFolder={uploadFolder}
            uploadContext={uploadContext}
            testPrefix={testPrefix}
            update={update}
            onChange={updateOne}
          />
        ))}
      </div>
    </div>
  );
}
